/**
 * Closed enum of learning-safe actions for the Student Tutor UI (S1-012).
 *
 * The Tutor UI in the MVP exposes ONLY these four actions. Free-text
 * prompts are not allowed: the design doc forbids any affordance that
 * lets a student request the answer outright, and the surest way to
 * enforce that at the platform level is to limit the action surface to
 * a closed list defined here in shared code.
 *
 * Adding a new action goes through `packages/shared` review, which is
 * the same review path that has to look at LMS and RBAC contracts.
 *
 * The forbidden phrases below are tracked in the meta bundle-scan test
 * (`apps/web/src/routes/tutor-copy-guard.test.ts`) so a future PR cannot
 * sneak them back into the UI bundle.
 */

export type SocraticActionId =
  | 'get_a_hint'
  | 'check_my_reasoning'
  | 'explain_the_concept'
  | 'guide_me_step_by_step';

export interface SocraticAction {
  readonly id: SocraticActionId;
  /** User-facing label. The label IS the request the tutor receives. */
  readonly label: string;
  /** One-line description for screen readers and tooltips. */
  readonly description: string;
}

export const SOCRATIC_ACTIONS: ReadonlyArray<SocraticAction> = [
  {
    id: 'get_a_hint',
    label: 'Get a hint',
    description:
      'Ask the tutor for one small nudge — never the full answer.',
  },
  {
    id: 'check_my_reasoning',
    label: 'Check my reasoning',
    description:
      'Share your current attempt; the tutor will tell you where the logic holds and where it slips.',
  },
  {
    id: 'explain_the_concept',
    label: 'Explain the concept',
    description:
      'Get a plain-language explanation of the idea behind the problem.',
  },
  {
    id: 'guide_me_step_by_step',
    label: 'Guide me step by step',
    description:
      'Work through the problem one prompt at a time — you do the thinking, the tutor checks each step.',
  },
];

/**
 * Phrases that must NEVER ship in the Tutor UI bundle. Kept here so the
 * shared package is the single source of truth for what the safety
 * grep-guard considers contraband.
 *
 * The list is intentionally small and verbatim — the bundle-scan test
 * does case-insensitive matching, so we do not need every casing.
 */
export const TUTOR_FORBIDDEN_PHRASES: ReadonlyArray<string> = [
  'Give me the answer',
  'Solve my homework',
  'Write my code',
];

export function isSocraticActionId(value: string): value is SocraticActionId {
  return SOCRATIC_ACTIONS.some((action) => action.id === value);
}
